// components/GuideRapportsStatistiques.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, HelpCircle, Users, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp, Info, BookOpen, BarChart2,
  Calendar, Clock, Activity, FileText, CreditCard, Coins, Percent, ClipboardList, Ghost
} from 'lucide-react';

const COLOR = '#448696';

const getIndicateursJour = () => [
  {
    icon: Users, color: '#448696', label: 'Employés actifs',
    desc: "Nombre total d'employés actifs enregistrés dans votre organisation à la date sélectionnée. Sert de référentiel pour le calcul de tous les taux.",
    exemple: "54 Employés actifs - votre organisation compte 54 employés en activité ce jour-là."
  },
  {
    icon: CheckCircle, color: '#22c55e', label: 'Présents',
    desc: "Nombre d'employés ayant au moins un pointage enregistré pour cette journée. Le pourcentage représente le taux de présence calculé sur l'effectif total.",
    exemple: "49 Présents - 90% de présence - 49 employés sur 54 ont pointé."
  },
  {
    icon: X, color: '#ef4444', label: 'Absents',
    desc: "Nombre d'employés officiellement déclarés absents. Inclut uniquement les absences explicitement enregistrées, pas les employés sans pointage.",
    exemple: "5 Absents - 10% d'absence - 5 employés ont été déclarés absents."
  },
  {
    icon: Clock, color: '#f59e0b', label: 'Retards',
    desc: "Nombre total de pointages présentant un statut de ponctualité anormal : retard simple, retard grave ou tolérance dépassée.",
    exemple: "3 Retards - 3 pointages d'arrivée enregistrés en dehors des horaires prévus."
  },
];

const getIndicateursMois = () => [
  {
    icon: Users, color: '#448696', label: 'Employés actifs',
    desc: "Nombre total d'employés actifs pour le mois sélectionné. Le sous-titre indique le nombre de départements distincts dans l'organisation.",
  },
  {
    icon: Coins, color: '#b08d1a', label: 'Masse salariale',
    desc: "Total des salaires bruts de l'ensemble des employés actifs pour le mois. Représente l'engagement financier mensuel avant déductions.",
    conseil: "Valeur de référence avant calcul des retenues, avances et charges."
  },
  {
    icon: CheckCircle, color: '#22c55e', label: 'Taux de présence',
    desc: "Moyenne des taux de présence journaliers calculée sur l'ensemble des jours ouvrés du mois.",
    exemple: "87% — en moyenne 87% des employés étaient présents chaque jour ouvré du mois."
  },
  {
    icon: Activity, color: '#448696', label: 'Jours analysés',
    desc: "Nombre de jours du mois pour lesquels des données de pointage ont été collectées. Les jours futurs et sans données sont exclus.",
  },
];

const getIndicateursPaie = () => [
  {
    icon: CreditCard, color: '#448696', label: 'Paiements effectués',
    desc: "Nombre total de fiches de paie générées pour le mois sélectionné.",
  },
  {
    icon: Coins, color: '#22c55e', label: 'Montant total payé',
    desc: "Somme totale des salaires nets versés aux employés ce mois-ci, après déduction de toutes les retenues et avances.",
    conseil: "Ce montant correspond au décaissement réel de l'organisation."
  },
  {
    icon: AlertTriangle, color: '#f59e0b', label: 'En attente',
    desc: "Nombre de fiches de paie générées mais non encore validées ni clôturées.",
    conseil: "Vérifiez et validez les paiements en attente avant la clôture du mois."
  },
  {
    icon: Percent, color: '#448696', label: 'Taux de couverture',
    desc: "Pourcentage d'employés pour lesquels une fiche de paie a été générée ce mois-ci.",
    conseil: "Un taux inférieur à 100% indique que des employés n'ont pas encore été traités."
  },
];

const getIndicateursAvances = () => [
  {
    icon: Coins, color: '#b08d1a', label: 'Avances accordées',
    desc: "Nombre total d'avances sur salaire accordées ce mois-ci, avec le nombre d'employés distincts concernés.",
  },
  {
    icon: Coins, color: '#448696', label: 'Montant total',
    desc: "Somme totale des avances accordées ce mois-ci en FCFA. Ce montant sera déduit des salaires nets correspondants.",
  },
  {
    icon: Percent, color: '#f59e0b', label: 'Taux moyen',
    desc: "Pourcentage moyen que représentent les avances par rapport aux salaires de base des employés concernés.",
    conseil: "Un taux moyen élevé peut signaler des difficultés financières récurrentes à adresser."
  },
];

const getCalendrierGuide = () => [
  { color: '#22c55e', bg: '#f0fff4', label: '>= 90% — Excellent', desc: "Taux de présence supérieur ou égal à 90%. La quasi-totalité des employés étaient présents." },
  { color: '#d97706', bg: '#fffbeb', label: '70–89% — Acceptable', desc: "Taux de présence entre 70% et 89%. Une proportion notable d'employés était absente. Vérifiez si des absences justifiées l'expliquent." },
  { color: '#ef4444', bg: '#fff5f5', label: '< 70% — Critique', desc: "Taux de présence inférieur à 70%. Plus d'un tiers des employés était absent. Situation nécessitant une analyse approfondie." },
  { color: '#9ca3af', bg: '#f9fafb', label: 'Week-end', desc: "Samedis et dimanches affichés en grisé. Aucune donnée de pointage attendue." },
];

const AccordionSection = ({ title, colorBar, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: colorBar }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBar + '15' }}>
            <Icon size={15} style={{ color: colorBar }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-2 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

const GuideCard = ({ icon: Icon, color, label, desc, exemple, conseil }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="flex items-start gap-3">
      {Icon ? (
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
          <Icon size={14} className="text-white" />
        </div>
      ) : (
        <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
      )}
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
        {exemple && (
          <p className="text-xs text-gray-400 mt-1.5">
            <span className="font-semibold text-gray-500">Ex :</span> {exemple}
          </p>
        )}
        {conseil && (
          <p className="text-xs mt-1.5 font-medium" style={{ color: COLOR }}>{conseil}</p>
        )}
      </div>
    </div>
  </div>
);

const GuideRapportsStatistiques = ({ onClose, comptaEnabled = false }) => {
  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between" style={{ backgroundColor: COLOR }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <ClipboardList size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">Guide de lecture</h2>
              <p className="text-xs text-white/70 mt-0.5">Interprétation des indicateurs</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <Info size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              Le module Rapports & Statistiques offre une vision synthétique des indicateurs RH.
              Trois vues sont disponibles : <span className="font-semibold text-gray-700">Vue Journalière</span> pour le suivi quotidien,
              <span className="font-semibold text-gray-700"> Vue Mensuelle</span> pour l'analyse des tendances,
              et <span className="font-semibold text-gray-700"> Employés fantômes</span> pour identifier les employés sans activité de pointage.
              {comptaEnabled && <span className="font-semibold text-gray-700"> Module comptabilité activé — données de paie et d'avances disponibles.</span>}
            </p>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          {/* Navigation */}
          <AccordionSection title="Modes de vue et navigation" colorBar={COLOR} icon={Calendar} defaultOpen={true}>
            <GuideCard
              icon={Calendar} color="#448696" label="Vue Journalière"
              desc="Affiche les indicateurs RH d'une journée précise. Naviguez jour par jour via les flèches ou revenez à la date du jour avec le bouton Aujourd'hui."
              conseil="Idéale pour un point quotidien sur les présences et les anomalies du jour."
            />
            <GuideCard
              icon={Activity} color="#b08d1a" label="Vue Mensuelle"
              desc="Analyse complète du mois sélectionné : vue d'ensemble, données de paie et avances (si activées), calendrier jour par jour. Naviguez de mois en mois via les flèches."
              conseil="Idéale pour les bilans mensuels, l'analyse des tendances et la préparation des clôtures."
            />
            <GuideCard
              icon={Users} color="#8b5158" label="Employés fantômes"
              desc="Vue dédiée aux employés actifs sans activité de pointage. Deux catégories : employés n'ayant jamais pointé depuis leur embauche, et employés inactifs depuis plus de 30 jours. Aucun sélecteur de date — les données sont toujours à jour."
              conseil="Consultez cette vue régulièrement pour détecter les anomalies RH et prendre les mesures nécessaires."
            />
            <GuideCard
              icon={FileText} color="#1f2937" label="Export PDF"
              desc="Génère un rapport PDF complet de la vue active (journalière ou mensuelle). Le fichier est automatiquement téléchargé sur votre poste de travail. Non disponible sur la vue Employés fantômes."
              conseil="Exportez le rapport en fin de mois pour archivage ou transmission à la direction."
            />
          </AccordionSection>

          {/* Vue journalière */}
          <AccordionSection title="Vue Journalière - indicateurs" colorBar="#22c55e" icon={CheckCircle} defaultOpen={false}>
            {getIndicateursJour().map((item) => (
              <GuideCard key={item.label} {...item} />
            ))}
            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 mt-1">
              <p className="text-xs font-semibold text-gray-600 mb-1">Barres de progression</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Les barres de progression visualisent les taux de présence et d'absence. Taux de présence = Présents ÷ Employés actifs × 100. Taux d'absence = 100% - Taux de présence.
              </p>
            </div>
          </AccordionSection>

          {/* Vue mensuelle */}
          <AccordionSection title="Vue Mensuelle - vue d'ensemble" colorBar={COLOR} icon={BarChart2} defaultOpen={false}>
            {getIndicateursMois().map((item) => (
              <GuideCard key={item.label} {...item} />
            ))}
          </AccordionSection>

          {/* Calendrier */}
          <AccordionSection title="Vue Mensuelle - calendrier de présence" colorBar={COLOR} icon={Calendar} defaultOpen={false}>
            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 mb-2">
              <p className="text-xs text-gray-500 leading-relaxed">
                Le calendrier affiche une ligne par jour du mois avec le nombre de présents, absents, retards et le taux de présence. Les jours futurs apparaissent grisés.
              </p>
            </div>
            <p className="text-xs font-semibold text-gray-600 px-1 mb-1">Codes couleur du taux de présence</p>
            {getCalendrierGuide().map((item) => (
              <div key={item.label} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/30">
                <div
                  className="px-2.5 py-1 rounded text-xs font-semibold flex-shrink-0 whitespace-nowrap"
                  style={{ backgroundColor: item.bg, color: item.color, border: `1px solid ${item.color}30` }}
                >
                  {item.label}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed pt-0.5">{item.desc}</p>
              </div>
            ))}
            <GuideCard
              color="#718096" label="Détail des absences (survol)"
              desc="Dans la colonne Absents, survolez un chiffre pour afficher le détail : employés explicitement déclarés absents et employés sans aucun pointage enregistré."
              conseil="Un employé sans pointage n'est pas nécessairement absent — il peut présenter un pointage incomplet."
            />
            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 mt-1">
              <p className="text-xs font-semibold text-gray-600 mb-2">Résumé du mois</p>
              <div className="space-y-1.5">
                {[
                  { label: 'Meilleur taux', desc: 'Jour du mois présentant le taux de présence le plus élevé.', color: '#22c55e' },
                  { label: 'Taux moyen', desc: 'Moyenne arithmétique de tous les taux journaliers du mois.', color: '#448696' },
                  { label: 'Pire taux', desc: 'Jour du mois présentant le taux de présence le plus faible.', color: '#ef4444' },
                  { label: 'Jours analysés', desc: 'Nombre de jours pour lesquels des données existent.', color: '#718096' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: item.color }} />
                    <p className="text-xs text-gray-500"><span className="font-semibold text-gray-700">{item.label} :</span> {item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </AccordionSection>

          {/* Employés fantômes */}
          <AccordionSection title="Employés fantômes - comment l'interpréter" colorBar="#8b5158" icon={Users} defaultOpen={false}>
            <div className="px-4 py-3 rounded-lg border mb-2" style={{ backgroundColor: '#f9f0f1', borderColor: '#c49097' }}>
              <p className="text-xs leading-relaxed" style={{ color: '#8b5158' }}>
                Cette vue identifie les employés actifs présentant une anomalie d'activité. Elle est indépendante de toute date - les données reflètent toujours l'état actuel.
              </p>
            </div>
            <GuideCard
              color="#8b5158" label="Jamais pointé"
              desc="Employés actifs n'ayant enregistré aucun pointage depuis leur date d'embauche. La colonne 'Jours sans pointage' indique le nombre de jours écoulés depuis l'embauche sans aucune trace d'activité."
              conseil="Un employé embauché depuis plusieurs semaines sans aucun pointage mérite une vérification urgente : absence non déclarée, erreur de fiche, départ non enregistré."
            />
            <GuideCard
              color="#f97316" label="Inactifs depuis 30 jours"
              desc="Employés ayant pointé dans le passé mais sans aucun pointage lors des 30 derniers jours. La colonne 'Dernier pointage' indique la dernière date d'activité connue."
              conseil="Un arrêt brutal d'activité peut indiquer un congé non enregistré, une suspension ou un départ de fait non formalisé."
            />
            <GuideCard
              color="#448696" label="Pagination des listes"
              desc="Chaque catégorie dispose de sa propre pagination (10 employés par page). Les deux listes sont indépendantes - paginer l'une n'affecte pas l'autre."
            />
            <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 mt-1">
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Actions recommandées</p>
              <div className="space-y-1.5">
                {[
                  { action: 'Contacter l\'employé', desc: 'Vérifier si l\'absence est justifiée et non déclarée.', color: '#448696' },
                  { action: 'Vérifier la fiche employé', desc: 'Confirmer que le badge RFID est bien configuré et actif.', color: '#f97316' },
                  { action: 'Enregistrer le départ', desc: 'Si l\'employé a quitté l\'entreprise, mettre à jour son statut dans le système.', color: '#8b5158' },
                  { action: 'Déclarer l\'absence', desc: 'Si l\'employé est en congé ou arrêt, l\'enregistrer officiellement via la vue Admin de pointage.', color: '#22c55e' },
                ].map((item) => (
                  <div key={item.action} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: item.color }} />
                    <p className="text-xs text-gray-500"><span className="font-semibold text-gray-700">{item.action} :</span> {item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </AccordionSection>

          {/* Paie */}
          {comptaEnabled && (
            <AccordionSection title="Vue Mensuelle - gestion de la paie" colorBar="#6366f1" icon={CreditCard} defaultOpen={false}>
              <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 mb-2">
                <p className="text-xs text-blue-600 leading-relaxed">
                  Section disponible uniquement avec le module comptabilité activé.
                </p>
              </div>
              {getIndicateursPaie().map((item) => (<GuideCard key={item.label} {...item} />))}
              <p className="text-xs font-semibold text-gray-600 px-1 mt-2 mb-1">Composition de la paie</p>
              {[
                { color: '#448696', label: 'Salaires bruts', desc: "Total des salaires fixes de base avant toute déduction." },
                { color: '#ef4444', label: 'Retenues', desc: "Total des déductions : absences, retards graves, cotisations et retenues configurées." },
                { color: '#f97316', label: 'Avances', desc: "Total des avances accordées ce mois-ci et déduites de la paie finale." },
                { color: '#22c55e', label: 'Salaires nets', desc: "Montant final versé : salaire brut - retenues - avances. Figure sur les fiches de paie." },
              ].map((item) => (<GuideCard key={item.label} color={item.color} label={item.label} desc={item.desc} />))}
              <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 mt-1">
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Statut des paiements</p>
                {[
                  { label: 'Clôturés', desc: 'Fiches validées et finalisées. Aucune modification possible.', color: '#22c55e' },
                  { label: 'En attente', desc: 'Fiches générées non encore validées. Action requise.', color: '#f59e0b' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-2 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: item.color }} />
                    <p className="text-xs text-gray-500"><span className="font-semibold text-gray-700">{item.label} :</span> {item.desc}</p>
                  </div>
                ))}
              </div>
            </AccordionSection>
          )}

          {/* Avances */}
          {comptaEnabled && (
            <AccordionSection title="Vue Mensuelle - avances sur salaire" colorBar="#d97706" icon={Coins} defaultOpen={false}>
              <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 mb-2">
                <p className="text-xs text-amber-700 leading-relaxed">
                  Section disponible uniquement avec le module comptabilité activé.
                </p>
              </div>
              {getIndicateursAvances().map((item) => (<GuideCard key={item.label} {...item} />))}
            </AccordionSection>
          )}

          {/* Bonne pratique */}
          <div className="px-5 py-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-2">Routine de consultation recommandée</p>
            <div className="space-y-1.5">
              {[
                { quand: 'Chaque matin', quoi: 'Vue Journalière - vérifiez les présences et détectez les anomalies du jour.' },
                { quand: 'Chaque semaine', quoi: 'Calendrier mensuel - repérez les jours avec des taux critiques.' },
                { quand: 'Fin de mois', quoi: 'Vue Mensuelle complète - validez les paiements en attente et exportez le rapport PDF.' },
                { quand: 'Régulièrement', quoi: 'Employés fantômes - identifiez les employés sans activité et prenez les mesures nécessaires.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: COLOR }} />
                  <p className="text-xs text-gray-500"><span className="font-semibold text-gray-700">{item.quand} :</span> {item.quoi}</p>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Module compta : <span className="font-semibold text-gray-600">{comptaEnabled ? 'Activé' : 'Non inclus de votre licence'}</span>
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Fermer
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GuideRapportsStatistiques;